import * as React from "react";
import { useEffect } from "react";
import { observer } from "mobx-react-lite";

import { structure } from "@/endpoints/dryp/structure";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { CUSTOM_FILTERS } from "@/config/list";
import { FilterableCheckboxGroupDropdown, type CheckboxGroup } from "@/components/FilterableCheckboxGroupDropdown";
import { ChevronDown, GroupIcon } from "lucide-react";
import { ListFilters } from "@/types/list";

type StructuresGroupFilterProps = {
	filter: ListFilters;
};

export const StructuresGroupFilter = observer(({ filter }: StructuresGroupFilterProps) => {
	const structureGroupsApi = useAsyncStateStore(structure.getAllStructureGroups);

	useEffect(() => {
		structureGroupsApi.fetch({ pageNumber: 0, pageSize: 2000 });
	}, []);

	const structureGroups = structureGroupsApi?.data?.items || [];
	const loading = structureGroupsApi?.loading;

	const selectedGroupIds = (filter.getFilter(CUSTOM_FILTERS.structureGroupIds) as string[] | undefined) ?? [];

	const groups: CheckboxGroup[] = React.useMemo(
		() => [
			{
				name: "Structure groups",
				items: structureGroups.map(structureGroup => ({
					id: structureGroup.id,
					name: structureGroup.name,
					checked: selectedGroupIds.includes(structureGroup.id),
				})),
			},
		],
		[structureGroups, selectedGroupIds],
	);

	const handleCheckedChange = (groupId: string, checked: boolean) => {
		const nextSelectedGroupIds = checked
			? Array.from(new Set([...selectedGroupIds, groupId]))
			: selectedGroupIds.filter(selectedGroupId => selectedGroupId !== groupId);

		filter.setFilter(CUSTOM_FILTERS.structureGroupIds, nextSelectedGroupIds);
	};

	const handleToggleAll = (checked: boolean) => {
		const nextSelectedGroupIds = checked ? structureGroups.map(structureGroup => structureGroup.id) : [];
		filter.setFilter(CUSTOM_FILTERS.structureGroupIds, nextSelectedGroupIds);
	};

	return (
		<FilterableCheckboxGroupDropdown
			groups={groups}
			onCheckedChange={handleCheckedChange}
			onToggleAll={handleToggleAll}
			loading={loading}
			buttonVariant="outline"
			buttonLabel="Groups"
			buttonClassName="flex items-center gap-2"
			renderItem={item => (
				<div className="flex items-center gap-2">
					<GroupIcon className="h-4 w-4 text-blue-400" />
					{item.name}
				</div>
			)}
			buttonContent={
				<span className="flex items-center gap-2">
					{selectedGroupIds.length > 0 ? (
						<span className="text-[11px] text-muted-foreground">({selectedGroupIds.length} selected)</span>
					) : (
						<span>All</span>
					)}
					<ChevronDown className="ml-2 h-4 w-4 opacity-50" />
				</span>
			}
			contentAlign="end"
			searchPlaceholder="Search structure groups..."
		/>
	);
});
